import type { UserProfileData } from '@contexts/AuthContextStore';

export type StepErrors = Partial<Record<keyof UserProfileData, string>>;

const OTHER = 'other';

/**
 * Validate a single step. The error map is keyed by `UserProfileData` field
 * names so the step components can render inline errors next to their inputs.
 *
 * Steps 0 and 1 are required, steps 2 and 3
 * (tool stacks) are optional — *except* the free-text "Please specify" field
 * which becomes mandatory whenever the user has picked the "Other" chip.
 */
export function validateStep(
  step: number,
  data: UserProfileData,
  required: string,
): StepErrors {
  switch (step) {
    case 0:
      return validateAboutYou(data, required);
    case 1:
      return validateIntent(data, required);
    case 2:
      return validateDesignStack(data, required);
    case 3:
      return validateMfgStack(data, required);
    default:
      return {};
  }
}

function validateAboutYou(data: UserProfileData, required: string): StepErrors {
  const errors: StepErrors = {};
  if (!data.firstName?.trim()) errors.firstName = required;
  if (!data.lastName?.trim()) errors.lastName = required;
  if (!data.companyName?.trim()) errors.companyName = required;
  if (!data.role) errors.role = required;
  if (!data.industry) errors.industry = required;
  if (!data.companySize) errors.companySize = required;
  return errors;
}

function validateIntent(data: UserProfileData, required: string): StepErrors {
  const errors: StepErrors = {};
  if (!data.intents || data.intents.length === 0) {
    errors.intents = required;
  }
  if (selectionContainsOther(data.intents) && !data.intentsOther?.trim()) {
    errors.intentsOther = required;
  }
  return errors;
}

function validateDesignStack(
  data: UserProfileData,
  required: string,
): StepErrors {
  const errors: StepErrors = {};
  requireOtherText(errors, required, data.cadSoftware, 'cadOther', data.cadOther);
  requireOtherText(errors, required, data.caeTools, 'caeOther', data.caeOther);
  requireOtherText(errors, required, data.plm, 'plmOther', data.plmOther);
  return errors;
}

function validateMfgStack(
  data: UserProfileData,
  required: string,
): StepErrors {
  const errors: StepErrors = {};
  requireOtherText(errors, required, data.camSoftware, 'camOther', data.camOther);
  requireOtherText(errors, required, data.mes, 'mesOther', data.mesOther);
  // Manufacturing setup is multi-select — "other" is baked into the options
  // array (rather than appended via `withOther`), but the check is the same:
  // when the selection contains "other", the free-text answer is required.
  requireOtherText(
    errors,
    required,
    data.mfgSetup,
    'mfgSetupOther',
    data.mfgSetupOther,
  );
  return errors;
}

function selectionContainsOther(values?: string[]): boolean {
  return Array.isArray(values) && values.includes(OTHER);
}

function requireOtherText(
  errors: StepErrors,
  required: string,
  selection: string[] | undefined,
  errorKey: keyof UserProfileData,
  text: string | undefined,
): void {
  if (selectionContainsOther(selection) && !text?.trim()) {
    errors[errorKey] = required;
  }
}

export function isValid(errors: StepErrors): boolean {
  return Object.keys(errors).length === 0;
}
